<template>
  <m-pageframe
    title="Bulk Message"
    :color="pageframe_color"
  >
    <v-container
      v-if="bulk_message != null"
      :class="{
        'px-6': message_state === 'edit',
        'pa-3 pa-md-6 container-capped-width': message_state !== 'edit',
      }"
      :fluid="message_state === 'edit'"
    >
      <!-- Warnings & Alerts That Conditionally Show Regardless of State -->
      <temp-disable-wineries-from-scheduling
        v-if="$rc.inIndustry('wine')"
      />

      <provision-warning-bulk-messages />

      <m-incident-banner
        ref="incident-banner"
        page="bulk_message"
      />

      <!-- State: Prepare/Review -->
      <template v-if="['prepare', 'review'].includes(message_state)">
        <review-state
          v-bind="common_state_props"
          :is_archived="is_archived"
          :message_state="message_state"
          :selected_recipient_filter="selected_recipient_filter"
          :progress_detail="progress_detail"
          :table_options.sync="table_options"
          @patch="onPatch"
          @split-message="onClickSplit"
          @cancel-review="cancelReview"
          @update="update"
          @select-test-message-recipient="selectTestMessageRecipient"
          @delete-recipient="deleteRecipient"
          @update-recipient-filter="onRecipientFilterChange"
          @update-recipients="updateRecipients"
          @view-imports="onClickViewImports"
          @update-full-text="onFullTextChange"
          @send-bulk-message="doSendMessage"
          @schedule-bulk-message="doScheduleMessage"
          @cancel-scheduled-send="doCancelScheduledSend"
          @export-recipients-to-excel="exportRecipientsToExcel"
          @update-progress-detail="updateProgressDetail"
        />
      </template>

      <!-- State: Send/Paused/Cancelled/Finished -->
      <template v-else-if="['send', 'paused', 'cancelled', 'finished'].includes(message_state)">
        <send-state
          v-bind="common_state_props"
          :message_state="message_state"
          :selected_recipient_filter="selected_recipient_filter"
          :progress_detail="progress_detail"
          :table_options.sync="table_options"
          @update="update"
          @update-recipient-filter="onRecipientFilterChange"
          @update-recipients="updateRecipients"
          @view-imports="onClickViewImports"
          @update-full-text="onFullTextChange"
          @pause="onClickPause"
          @resume="onClickResume"
          @cancel="onClickCancel"
          @export-recipients-to-excel="exportRecipientsToExcel"
          @update-progress-detail="updateProgressDetail"
          @change-outcome="changeOutcome"
        />
      </template>

      <!-- State: Edit -->
      <template v-else>
        <edit-state
          v-bind="common_state_props"
          :use_outcome_tracking="use_outcome_tracking"
          :outcomes="outcomes"
          :has_cancellation_reason="has_cancellation_reason"
          :use_old_message_patterns="use_old_message_patterns"
          :old_message_text="old_message_text"
          :table_options.sync="table_options"
          @patch="onPatch"
          @split-message="onClickSplit"
          @update="update"
          @delete-recipient="deleteRecipient"
          @update-recipients="updateRecipients"
          @update-full-text="onFullTextChange"
          @recipient-validation="onClickValidation"
          @view-imports="onClickViewImports"
          @export-recipients-to-excel="exportRecipientsToExcel"
          @upsert-bulk-message="upsertBulkMessage"
          @select-test-message-recipient="selectTestMessageRecipient"
          @example-message-changed="onExampleMessageChanged"
          @create-outcome="onClickCreateOutcome"
          @after-edit-outcome="afterEditOutcome"
          @after-delete-outcome="afterDeleteOutcome"
          @change-state="onChangeState"
        />
      </template>

      <!-- Dialogs -->
      <m-bulk-message-search-imports-dialog ref="bulk-message-search-imports-dialog" />

      <m-bulk-outcome-create-dialog
        ref="create-outcome-dialog"
        @ok="onCreateOutcome"
      />

      <m-bulk-outcome-change-dialog
        ref="change-outcome-dialog"
        @ok="onChangeOutcome"
      />

      <m-send-test-message-dialog
        ref="send-test-message-dialog"
        @send-test-message="onSendTestMessage"
      />

      <bulk-message-state-monitor
        v-if="bulk_message != null"
        ref="state-monitor"
        :bulk_message_id="bulk_message_id"
        :current_state="message_state"
        :current_scheduled_send_time="bulk_message.scheduled_send_time"
      />

      <temp-disable-wineries-from-scheduling-modal v-if="$rc.inIndustry('wine')" />
    </v-container>
  </m-pageframe>
</template>

<script>
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import ProvisionWarningBulkMessages from '@/components/bulk/provision-warning-bulk-messages.vue';
import EditState from "@/components/bulk/states/edit.vue";
import ReviewState from '@/components/bulk/states/review.vue';
import SendState from '@/components/bulk/states/send.vue';
import BulkMessageStateMonitor from '@/components/bulk/bulk-message-state-monitor.vue';
import TempDisableWineriesFromScheduling from '@/components/bulk/temp-disable-wineries-from-scheduling.vue';
import TempDisableWineriesFromSchedulingModal from '@/components/bulk/temp-disable-wineries-from-scheduling-modal.vue';

const default_table_options = {
  page: 1,
  itemsPerPage: 50,
  sortBy: ['bulk_message_recipient_id'],
  sortDesc: [false],
  groupBy: [],
  groupDesc: [],
  multiSort: false,
  mustSort: false,
};

export default
  {
    components: {
      ProvisionWarningBulkMessages,
      BulkMessageStateMonitor,
      EditState,
      ReviewState,
      SendState,
      TempDisableWineriesFromScheduling,
      TempDisableWineriesFromSchedulingModal,
    },

    data() {
      return {
        bulk_message: null,
        current_example_message: null,

        // recipients
        full_text: null,
        loading_recipients: false,
        selected_recipient_filter: '',
        recipients: [],
        num_recipients_under_current_filter: 0,
        recipient_extra_fields: [],
        use_outcome_tracking: null,
        outcomes: [],

        // progress state
        progress_detail: null,

        // template
        table_options: { ...default_table_options },
      }
    },

    mounted() {
      this.update();
    },

    methods: {
      stopStatePolling() {
        this.$refs['state-monitor']?.stopPolling();
      },

      suppressStatePollTemporarily() {
        this.$refs['state-monitor']?.suppressPollTemporarily();
      },

      executeStateChangingAction(apiCall) {
        this.suppressStatePollTemporarily();
        return apiCall();
      },

      update({ update_recipients = true, after_update = null } = {}) {
        this.$rc.rest(
          'GET: /api/v1/bulk/messages/get-profile/:bulk_message_id',

          { bulk_message_id: this.bulk_message_id },

          (data) => {
            const previous_state = this.message_state;
            this.bulk_message = data;
            const is_new_state = previous_state && data.state !== previous_state;

            if (is_new_state) {
              this.selected_recipient_filter = this.default_recipient_filter;
              this.full_text = null;
              this.table_options = { ...default_table_options }; // table_options watcher will trigger updateRecipients

              if (previous_state === 'edit') {
                // progress_detail is hoisted above conditional rendering, so need to manually avoid showing stale data when transitioning from edit -> prepare
                // progress will immediately be re-fetched when recipients-tracker is re-mounted
                this.progress_detail = {};
              }
            } else if (update_recipients) {
              this.selected_recipient_filter = this.default_recipient_filter;
              this.updateRecipients();
            }

            this.updateOutcomes();

            if (this.bulk_message) {
              if (this.bulk_message.use_outcome_tracking) {
                this.use_outcome_tracking = 'yes';
              }
              else {
                this.use_outcome_tracking = 'no';
              }
            }
            if (after_update && typeof after_update === 'function') {
              after_update();
            }
          },

          () => {
            this.$rc.alert('Error', 'Unable to get bulk message');
          },
        );
      },

      onPatch(patch, { on_success, on_error } = {}) {
        this.bulk_message = {
          ...this.bulk_message,
          ...patch
        };

        this.$rc.showSpinner();

        this.$rc.putBulkMessage(
          this.bulk_message,
          (data) => {
            this.$rc.hideSpinner();
            this.update({ update_recipients: false });
            if (on_success && typeof on_success === 'function') on_success(data);
          },
          (err) => {
            this.$rc.hideSpinner();
            if (on_error && typeof on_error === 'function') on_error();
          },
        );
      },

      upsertBulkMessage(child_bulk_message, { on_success, on_error } = {}) {
        this.$rc.showSpinner();

        this.$rc.putBulkMessage(
          // can be called either from a child component or this component
          child_bulk_message || this.bulk_message,
          (data) => {
            this.$rc.hideSpinner();

            // since it came from child, parent needs to update
            if (child_bulk_message) {
              this.update();
            }
            if (on_success && typeof on_success === 'function') on_success(data);
          },
          (err) => {
            this.$rc.hideSpinner();
            if (on_error && typeof on_error === 'function') on_error();
          },
        );
      },

      doScheduleMessage(specimen_bulk_message_attestation, after_schedule) {
        this.executeStateChangingAction(() => this.$rc.rest({
          spinner: true,
          endpoint: 'PUT: /api/v1/bulk/messages/schedule-bulk-message',

          params: {
            ...this.bulk_message,
            specimen_bulk_message_attestation,
          },

          on_success: (data) => {
            if (data.attestation_signature_error_message != null) {
              this.$rc.alert('Unable to schedule bulk message', data.attestation_signature_error_message);
              this.update();
              return;
            }

            this.$rc.alert('Warning', 'You have scheduled a bulk message to be sent at a future time.<br/><br/>Consumers expect someone to be around to answer responses to bulk messages.  Make sure you are prepared to do this.<br/><br>Sending time is approximate (may be delayed by up to 5 minutes).');
          
            if (after_schedule && typeof after_schedule === 'function') {
              this.$nextTick(after_schedule)
            }
          },

          on_error: 'Unable to complete API call',
        }));
      },

      doSendMessage(specimen_bulk_message_attestation) {
        this.executeStateChangingAction(() => this.$rc.rest({
          spinner: true,
          endpoint: 'PUT: /api/v1/bulk/messages/send',

          params: {
            bulk_message_id: this.bulk_message_id,
            specimen_bulk_message_attestation,
          },

          on_success: (data) => {
            if (data.attestation_signature_error_message != null) {
              this.$rc.alert('Unable to send bulk message', data.attestation_signature_error_message);
              return;
            }

            this.update({
              after_update: () => window.scrollTo(0, 0),
            });
          },

          on_error: 'Unable to send bulk message.',
        }));
      },

      doCancelScheduledSend() {
        this.executeStateChangingAction(() => {
          const previous_scheduled_send_time = this.bulk_message.scheduled_send_time;
          const previous_scheduled_by_employee_id = this.bulk_message.scheduled_by_employee_id;
          this.bulk_message.scheduled_send_time = null;
          this.bulk_message.scheduled_by_employee_id = null;

          this.upsertBulkMessage(null, {
          on_success: () => {
            this.$rc.snackbar('Scheduled send cancelled');
          },
          on_error: () => {
            this.bulk_message.scheduled_send_time = previous_scheduled_send_time;
            this.bulk_message.scheduled_by_employee_id = previous_scheduled_by_employee_id;
          },
          });
        });
      },

      cancelReview() {
        this.executeStateChangingAction(() => this.$rc.rest({
          spinner: true,
          endpoint: 'PUT: /api/v1/bulk/messages/cancel-review',

          params: {
            bulk_message_id: this.bulk_message_id,
            user_action_id: uuidv4()
          },

          on_success: (data) => {
            this.update({
              after_update: () => window.scrollTo(0, 0),
            });
          },

          on_error: () => {
            this.$rc.alert('Error', 'Unable to cancel bulk message review');
          },
        }));
      },

      prepare() {
        this.executeStateChangingAction(() => this.$rc.rest({
          spinner: true,
          endpoint: 'PUT: /api/v1/bulk/messages/prepare',

          params: {
            bulk_message_id: this.bulk_message_id,
            user_action_id: uuidv4()
          },

          on_success: (data) => {
            this.update({
              after_update: () => window.scrollTo(0, 0),
            });
          },

          on_error: 'Unable to begin preparing bulk message',
        }));
      },

      onChangeState(new_state) {
        const new_state_actions = {
          prepare: this.prepare,
        }

        if (new_state_actions[new_state]) {
          new_state_actions[new_state]();
        }
      },

      onClickPause(after_pause) {
        this.executeStateChangingAction(() => this.$rc.rest({
          spinner: true,
          endpoint: 'PUT: /api/v1/bulk/pause',

          params: {
            bulk_message_id: this.bulk_message_id,
            user_action_id: uuidv4()
          },

          on_success: (data) => {
            this.update();

            if (after_pause && typeof after_pause === 'function') {
              this.$nextTick(after_pause)
            } else {
              this.$rc.snackbar('Sending paused');
            }
          },

          on_error: 'Unable to pause bulk message',
        }));
      },

      onClickResume(after_resume) {
        this.executeStateChangingAction(() => this.$rc.rest({
          spinner: true,
          endpoint: 'PUT: /api/v1/bulk/resume',

          params: {
            bulk_message_id: this.bulk_message_id,
            user_action_id: uuidv4()
          },

          on_success: (data) => {
            this.update();

            if (after_resume && typeof after_resume === 'function') {
              this.$nextTick(after_resume)
            }

            this.$rc.snackbar('Sending resumed');
          },

          on_error: 'Unable to resume bulk message',
        }));
      },

      onClickCancel(after_cancel) {
        this.executeStateChangingAction(() => this.$rc.rest({
          spinner: true,
          endpoint: 'PUT: /api/v1/bulk/cancel',

          params: {
            bulk_message_id: this.bulk_message_id,
          },

          on_success: (data) => {
            this.update();

            if (after_cancel && typeof after_cancel === 'function') {
              this.$nextTick(after_cancel)
            }

            this.$rc.snackbar('Sending this bulk message has been cancelled');
          },

          on_error: 'Unable to cancel bulk message',
        }));
      },

      // RECIPIENT MANAGEMENT
      onFullTextChange(new_full_text) {
        this.full_text = new_full_text;
        this.table_options = { ...default_table_options }; // back to page 1
        // table_options watcher will trigger updateRecipients
      },

      onRecipientFilterChange(new_filter) {
        this.selected_recipient_filter = new_filter;
        this.table_options = { ...default_table_options }; // back to page 1
        // table_options watcher will trigger updateRecipients
      },

      onTableOptionsChange() {
        this.updateRecipients();
      },

      updateRecipients() {
        const params = {
          ...this.$rc.parseVTableOptions(this.table_options, 'recipient_contact_last_name:desc:nulls-last'),
          full_text: this.full_text,
          organization_id: this.organization_id,
          bulk_message_id: this.bulk_message_id,
        }

        if (this.selected_recipient_filter) {
          params.state = this.selected_recipient_filter;
        }

        this.loading_recipients = true;

        this.$rc.rest({
          spinner: false,
          endpoint: 'LIST: /api/v1/bulk/recipients/search',
          params,

          on_success: (data) => {
            this.recipients = data.results;
            this.num_recipients_under_current_filter = data.result_count;
            this.recipient_extra_fields = data.recipient_extra_fields;
            this.loading_recipients = false;
          },

          on_error: () => {
            this.loading_recipients = false;
            this.$rc.alert('Error', 'Unable to list recipients for this bulk message');
          },
        });
      },

      deleteRecipient(recipient) {
        if (recipient.sent_time != null) {
          this.$rc.alert(
            'Unable to Delete Recipient',
            'This recipient has already received the bulk message and cannot be deleted.'
          );

          return;
        }

        this.$rc.confirm(
          'Delete Recipient',
          'Are you sure you want to delete this recipient?',
          [
            { text: 'Cancel', value: 'cancel' },
            { text: 'Confirm', value: 'confirm' },
          ],
          (response) => {
            if (response === 'confirm') {
              this.$rc.rest({
                spinner: true,
                endpoint: 'DELETE: /api/v1/bulk/recipients/one/:bulk_message_recipient_id',
                params: {
                  bulk_message_recipient_id: recipient.bulk_message_recipient_id
                },

                on_success: (data) => {
                  this.updateRecipients();
                  this.updateProgressDetail();
                },

                on_error: 'There was an error deleting the recipient. Please try again.',
              });
            }
            else {
              return;
            }
          }
        );
      },

      selectTestMessageRecipient(recipient) {
        if (this.bulk_message.from_endpoint == null) {
          this.$rc.alert('Unable to send test message', 'You need to select the phone number that will be used to send the bulk message message <b>before</b> you can send a test message.');
          return;
        }

        if (this.old_message_text == null) {
          this.$rc.alert('Unable to send test message', 'You need to enter the message you would like to send <b>before</b> you can send a test message.');
          return;
        }

        this.$refs['send-test-message-dialog'].showDialog(
          recipient.bulk_message_recipient_id,
          recipient.recipient_contact_first_name,
          recipient.recipient_contact_last_name
        );
      },

      onSendTestMessage(recipient_id, endpoint, prepend_test) {
        this.$rc.rest({
          spinner: true,
          endpoint: 'SEND: /api/v1/bulk/messages/send-test',
          timeout: 30000,

          params: {
            bulk_message_id: this.bulk_message_id,
            bulk_message_recipient_id: recipient_id,
            to_endpoint: endpoint,
            prepend_test,
          },

          on_success: (data) => {
            this.$rc.alert(
              'Message Sent',
              'Test message was successfully sent',
            );
          },

          on_error: 'There was an error sending the test message. Please try again.',
        });
      },

      exportRecipientsToExcel() {
        this.$rc.excelExport(
          'EXPORT: /api/v1/bulk/recipients/export/:organization_id/:bulk_message_id',
          {
            organization_id: this.organization_id,
            bulk_message_id: this.bulk_message_id,
          }
        );
      },

      onExampleMessageChanged(example_message) {
        this.current_example_message = example_message;
      },

      updateProgressDetail(on_success) {
        if (this.$refs['state-monitor']?.isTimedOut()) return;

        this.$rc.rest({
          spinner: false,
          endpoint: 'GET: /api/v1/bulk/messages/:bulk_message_id/progress-detail',

          params: {
            bulk_message_id: this.bulk_message_id,
          },

          on_success: (data) => {
            this.progress_detail = data;
            if (on_success) {
              on_success(data);
            }
          },

          on_error: 'Unable to load bulk message preparation progress details',
        });
      },

      onClickViewImports() {
        this.$refs['bulk-message-search-imports-dialog'].showDialog();
      },

      onClickValidation() {
        this.$router.push({
          name: 'search-endpoint-validation-details',
          params: {
            organization_id: this.organization_id
          }
        });
      },

      // OUTCOME MANAGEMENT
      onClickCreateOutcome() {
        const outcome_id = uuidv4();
        this.$refs['create-outcome-dialog'].showDialog(outcome_id);
      },

      onCreateOutcome(outcome_id, label) {
        // verify the label isn't already being used (no duplicate labels)
        for (let i = 0; i < this.outcomes.length; i++) {
          if (label.toLowerCase() === this.outcomes[i].label.toLowerCase()) {
            this.$rc.alert(
              'Outcome Already Exists',
              'There already exists an outcome with the name: ' + label + '. Names must be unique.',
            );
            return;
          }
        }

        const outcome = {
          organization_id: this.organization_id,
          bulk_message_id: this.bulk_message_id,
          outcome_id,
          label,
        };

        this.$rc.upsertBulkMessageOutcome(
          outcome,
          (data) => {
            this.updateOutcomes();
          },
          () => {
            this.$rc.alert(
              'Error Creating Outcome',
              'There was an error creating the outcome. Please try again.'
            );
          }
        );
      },

      afterEditOutcome(label_change, outcome) {
        outcome.label = label_change;

        this.$rc.upsertBulkMessageOutcome(
          outcome,
          (data) => {
            this.updateOutcomes();
          },
          () => {
            this.$rc.alert('Error Updating Outcome', 'There was an error updating the outcome, please try again.');
          },
        );
      },

      updateOutcomes() {
        if (this.bulk_message) {
          const search = {
            'bulk_message_id': this.bulk_message_id,
          };

          this.$rc.listBulkMessageOutcomes(
            search,
            (data) => {
              this.outcomes = data.results;
            },
            () => {
              this.$rc.alert(
                'Error Retrieving Outcomes',
                'There was an error retrieving outcomes for this message. Please try again.'
              );
            },
          );
        }
      },

      afterDeleteOutcome(event, outcome) {
        this.$rc.rest({
          spinner: true,
          endpoint: 'DELETE: /api/v1/bulk/messages/outcome',

          params: {
            bulk_message_id: outcome.bulk_message_id,
            outcome_id: outcome.outcome_id,
          },

          on_success: (data) => {
            if (data.failed_in_use) {
              this.$rc.alert(
                'Unable to Delete Outcome',
                'Outcomes that are in use cannot be deleted. Remove the usages of this outcome in order to delete it.'
              );
            }

            this.updateOutcomes();
          },

          on_error: 'Unable to delete outcome',
        });
      },

      changeOutcome(recipient) {
        const search = {
          bulk_message_id: this.bulk_message_id,
        };

        this.$rc.listBulkMessageOutcomes(
          search,
          (data) => {
            const outcomes = [{ text: 'Not Selected', value: null }];

            for (let i = 0; i < data.results.length; i++) {
              outcomes.push({
                text: data.results[i].label,
                value: data.results[i].outcome_id,
              });
            }

            this.$refs['change-outcome-dialog'].showDialog(
              recipient.bulk_message_recipient_id,
              recipient.outcome_id,
              outcomes
            );
          }
        );
      },

      onChangeOutcome(bulk_recipient_id, new_outcome_id) {
        this.$rc.updateBulkRecipientOutcome(
          bulk_recipient_id,
          new_outcome_id,
          (data) => {
            this.updateRecipients();
          },
          () => {
            this.$rc.alert(
              'Error Setting Outcome',
              'There was an error setting the outcome for the recipient. Please try again.'
            );
          }
        );
      },

      // SPLITTING
      onClickSplit() {
        if (this.bulk_message.scheduled_send_time != null) {
          this.$rc.alert('Unable to split bulk message', 'You may not split a bulk message that has a scheduled send time.<br/><br/>To split this bulk message, first cancel the scheduled send time (edit the message and click the cancel scheduled send button).');
          return;
        }

        this.$rc.prompt({
          title: 'Split bulk message',
          text: 'Once split, you will be redirected to the search screen where you can see the new messages.',
          label: 'Enter the number of parts to split the bulk message into (2 to 20)',
          value: 2,
          after_prompt: (value) => {
            this.doSplit(parseInt(value));
          },

          is_valid: (value) => {
            if (value == null) return false;

            let as_int = parseInt(value);

            if (isNaN(as_int))
              return false;

            if (value < 2) return false;
            if (value > 20) return false;

            return true;
          }
        });
      },

      doSplit(part_count) {
        this.update();
        this.stopStatePolling(); // since we won't be auto-redirecting after state change, stop polling to avoid confusing dialog

        this.$rc.startProcess({
          type: 'bulk-message-split',

          params: {
            bulk_message_id: this.bulk_message_id,
            part_count,
          },

          title: `Splitting ${this.bulk_message.label} into ${part_count} parts`,
          finished_button_text: 'Ok',
          failed_button_text: 'Ok',

          status: (pte) => {
            switch (pte.state) {
              case 'failed': return 'Unable to split bulk message';
              case 'finished': return `Your bulk message has been split into ${part_count} parts.  The original message has been archived. You will now be redirected to the search screen where you can see the new messages.`;
            }

            return `Bulk Message recipients split: ${pte.progress_counter}`;
          },

          on_finished: (pte) => {
            this.$router.push({
              name: 'search-bulk-messages',
              params: {
                organization_id: this.organization_id,
              },
            });
          },

          on_failed: (pte) => {
            this.$rc.alert('Error', 'There was an error splitting the bulk message. Please try again.');
          },
        });
      },
    },

    computed: {
      organization_id() {
        return this.$route.params.organization_id;
      },

      bulk_message_id() {
        return this.$route.params.bulk_message_id;
      },

      message_state() {
        if (this.bulk_message == null) return null;
        return this.bulk_message.state;
      },

      default_recipient_filter() {
        const defaults = {
          edit: null,
          prepare: 'ready_to_send',
          review: 'ready_to_send',
          send: 'sent',
          paused: 'sent',
          cancelled: 'sent',
          finished: 'sent',
        };
        return defaults[this.message_state] || null;
      },

      is_archived() {
        if (this.bulk_message == null) return false;
        return this.bulk_message.is_archived;
      },

      is_scheduled_but_unsent() {
        if (this.bulk_message == null) return false;
        if (this.bulk_message.sent_time != null) return false;
        if (this.bulk_message.scheduled_send_time != null) return true;

        return false;
      },

      message_type_force_mms() {
        if (!this.bulk_message) return false;
        if (this.bulk_message.blob_ids == null) return false;
        return this.bulk_message.blob_ids.length > 0;
      },

      message_type() {
        if (this.message_type_force_mms || this.bulk_message.vcard_id != null) return 'MMS';

        // Use current example message from the message patterns component if available
        if (this.current_example_message && this.current_example_message !== 'Could not generate a message') {
          return this.$rc.messageType(this.current_example_message, this.message_type_force_mms || this.bulk_message.vcard_id != null);
        }

        if (this.old_message_text == null) return 'SMS';
        return this.$rc.messageType(this.old_message_text);
      },

      pageframe_color() {
        if (this.organization_id !== this.$rc.$state.organization_id)
          return '#5D647C';
        else
          return '#F24653';
      },

      has_sent() {
        if (this.bulk_message == null) return false;
        return this.bulk_message.sent_time != null;
      },

      has_cancellation_reason() {
        if (!this.bulk_message) return false;
        return this.has_sent && this.bulk_message.state === 'cancelled' && this.bulk_message.cancellation_reason != null;
      },

      old_message_text() {
        if (!this.bulk_message) return null;
        if (this.bulk_message.message_patterns.length == 0) return null;

        let ret = this.bulk_message.message_patterns[0];
        if (ret == null) return null;

        ret = ret.trim();
        if (ret.length == 0) return null;

        return ret;
      },

      use_old_message_patterns() {
        if (!this.bulk_message) return false;
        return this.bulk_message.use_legacy_variables;
      },

      common_state_props() {
        return {
          organization_id: this.organization_id,
          bulk_message: this.bulk_message,
          bulk_message_id: this.bulk_message_id,
          full_text: this.full_text,
          loading_recipients: this.loading_recipients,
          recipients: this.recipients,
          recipient_extra_fields: this.recipient_extra_fields,
          num_recipients_under_current_filter: this.num_recipients_under_current_filter,
          has_sent: this.has_sent,
          is_scheduled_but_unsent: this.is_scheduled_but_unsent,
          message_type_force_mms: this.message_type_force_mms,
          message_type: this.message_type,
        };
      },
    },

    watch: {
      table_options: {
        handler() {
          this.onTableOptionsChange();
        },
        deep: true,
      },
    },
  }
</script>

<style lang="scss" scoped>
.container-capped-width {
  max-width: 1600px;
}

h2,
:deep(h2) {
  line-height: 1.4;
  font-size: 1.4rem;
}

a,
:deep(a) {
  color: var(--v-primary-base) !important;
}
</style>